// SETTINGS V2 — one read-only summary section on the Doprava page
// (SETTINGS_V2_SP9 §5, designer ShipMethod settings-pages-c.jsx 106-126). Method
// label + active region rows (regionLabel + from–to businessDays + PriceFormat),
// all-inactive hint (composed cleanly — no legacy comma/period surgery), or the
// self-pickup warehouse block (null-guarded). Inline styles + scoped CSS-var
// tokens; PriceFormat is a shared display formatter (exempt). No MUI/sx/dt/Tailwind.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import PriceFormat from "../../../../shared/components/formats/PriceFormat";
import { ShippingOptionCode } from "../../../../shared/models/models";
import {
  ShippingRegionOption,
  Warehouse,
} from "../../../shared/models/customerShippingOptions";
import {
  compressWeekDaysLocalized,
  formatHHmm,
  methodLabel,
  regionLabel,
} from "./shippingV2Utils";

export interface ShippingMethodSectionV2Props {
  code: ShippingOptionCode;
  options: ShippingRegionOption[];
  countryId: number;
  warehouse?: Warehouse;
  isPhone: boolean;
}

const ShippingMethodSectionV2: React.FC<ShippingMethodSectionV2Props> = ({
  code,
  options,
  countryId,
  warehouse,
  isPhone,
}) => {
  const { translate: t } = useTranslation("settings");

  const isPickup = code === ShippingOptionCode.selfPickUp;
  const typeOptions = options.filter((x) => x.typeCode === code);
  const activeRows = typeOptions
    .filter((x) => x.isActive)
    .sort((a, b) => a.regionCode - b.regionCode);
  const hasActive = activeRows.length > 0;
  const label = methodLabel(code, t);

  const renderBody = () => {
    if (!hasActive) {
      return (
        <div style={{ fontSize: 12.5, color: "var(--faint)", lineHeight: 1.55 }}>
          {t("settings:noActiveShipping", { shippingType: label })}{" "}
          {t("settings:addEditShippingHint")}{" "}
          <span style={{ fontWeight: 700, color: "var(--ink-2)" }}>
            {t("settings:editShippingOptions")}
          </span>
          .
        </div>
      );
    }

    if (isPickup) {
      // Active pickup WITHOUT a warehouse renders nothing (parity §5).
      if (!warehouse) return null;
      return (
        <div>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--ink-2)" }}>
            {t("settings:warehouse")}
          </div>
          <div style={{ fontSize: 12.5, color: "var(--ink)", marginTop: 2 }}>
            {warehouse.address}
          </div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--muted)", marginTop: 2 }}>
            {compressWeekDaysLocalized(warehouse.daysOfWeek || [], t)}, {formatHHmm(warehouse.opensAt)}–{formatHHmm(warehouse.closesAt)}
          </div>
        </div>
      );
    }

    return (
      <>
        {activeRows.map((opt) => (
          <div
            key={opt.regionCode}
            style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}
          >
            <span style={{ width: 120, fontSize: 12.5, color: "var(--ink-2)" }}>
              {regionLabel(opt.regionCode, countryId, t)}
            </span>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--muted)" }}>
              {t("settings:businessDaysRange", { from: opt.fromBusinessDays, count: opt.toBusinessDays })}
            </span>
            <span style={{ flex: 1 }} />
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, fontWeight: 600, color: "var(--ink)" }}>
              <PriceFormat value={opt.orderPrice?.value} />
            </span>
          </div>
        ))}
      </>
    );
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 14,
        padding: "13px 16px",
        borderTop: "1px solid var(--border-soft)",
        flexDirection: isPhone ? "column" : "row",
      }}
    >
      <span
        style={{
          width: isPhone ? "auto" : 120,
          flexShrink: 0,
          fontSize: 13.5,
          fontWeight: 600,
          color: "var(--ink)",
        }}
      >
        {label}
      </span>
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 5 }}>
        {renderBody()}
      </div>
    </div>
  );
};

export default ShippingMethodSectionV2;
